import React, { useState } from "react";
import { Box, TextField, Autocomplete, Typography } from "@mui/material";
import {
  Controller,
  type Control,
  type UseFormSetValue,
  type UseFormWatch,
} from "react-hook-form";
import { type FormData } from "./formSchema";
import { type MapPlotRequest } from "../../types";

import { format } from "date-fns";

interface MapPlotRequestNameOption {
  label: string; // 表示用（重複時は更新日時付き）
  value: number; // map_plot_request_id
  name: string; // 識別名（API送信用）
  updatedAt: string; // 更新日時
}

interface MapPlotRequestNameSelectorProps {
  control: Control<FormData>;
  setValue: UseFormSetValue<FormData>;
  watch: UseFormWatch<FormData>;
  existingRequests: MapPlotRequest[];
  required?: boolean;
  isLoading?: boolean;
}

/**
 * 識別名の選択/入力を管理するコンポーネント
 * Autocompleteで既存の識別名を選択するか、新規に入力できる
 */
export const CMapPlotRequestNameSelector: React.FC<
  MapPlotRequestNameSelectorProps
> = ({
  control,
  setValue,
  watch,
  existingRequests,
  required = true,
  isLoading = false,
}) => {
  const [inputValue, setInputValue] = useState("");
  const currentName = watch("name");
  const currentMapPlotRequestId = watch("mapPlotRequestId");

  // 識別名でグループ化して重複を検出
  const nameOptions: MapPlotRequestNameOption[] = React.useMemo(() => {
    const nameCounts = existingRequests.reduce<Record<string, number>>(
      (acc, req) => {
        acc[req.name] = (acc[req.name] || 0) + 1;
        return acc;
      },
      {}
    );

    return existingRequests
      .map((request) => {
        const isDuplicate = nameCounts[request.name] > 1;
        const formattedDate = format(
          new Date(request.updatedAt),
          "yyyy/MM/dd HH:mm:ss"
        );

        return {
          label: isDuplicate
            ? `${request.name} (更新日時: ${formattedDate})`
            : request.name,
          value: request.id,
          name: request.name,
          updatedAt: request.updatedAt,
        };
      })
      .sort((a, b) => {
        // 更新日時で降順ソート（新しいものが上に表示される）
        return (
          new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime()
        );
      });
  }, [existingRequests]);

  // パフォーマンス最適化: ラベルのSet（O(1)での存在チェック用）
  const nameOptionLabels = React.useMemo(
    () => new Set(nameOptions.map((opt) => opt.label)),
    [nameOptions]
  );

  // 既存の識別名か新規かを判定してメッセージを生成
  const getHelperMessage = (): string => {
    if (!currentName) {
      return "";
    }

    // 既存の識別名を選択した場合
    if (currentMapPlotRequestId) {
      return `既存の「${currentName}」に追加されます`;
    }

    // 新規の識別名を入力した場合
    return `新しい識別名「${currentName}」で保存されます`;
  };

  const handleChange = (
    _event: React.SyntheticEvent,
    value: string | MapPlotRequestNameOption | null
  ): void => {
    if (typeof value === "string") {
      // 手入力の場合
      setValue("name", value);
      setValue("mapPlotRequestId", undefined);
    } else if (value) {
      // 既存の識別名を選択した場合
      setValue("name", value.name);
      setValue("mapPlotRequestId", value.value);
    } else {
      // クリアした場合
      setValue("name", "");
      setValue("mapPlotRequestId", undefined);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", flex: 1 }}>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold" }}>
        識別名{" "}
        {required && (
          <span style={{ color: "red", fontSize: "12px" }}>※必須</span>
        )}
      </Typography>

      <Controller
        name="name"
        control={control}
        render={({ fieldState }) => (
          <Autocomplete
            freeSolo
            fullWidth
            options={nameOptions}
            loading={isLoading}
            loadingText="読み込み中..."
            disablePortal
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.label
            }
            inputValue={inputValue}
            onInputChange={(_event, newInputValue) => {
              setInputValue(newInputValue);
              // 手入力時はnameを更新、mapPlotRequestIdはクリア
              if (!nameOptionLabels.has(newInputValue)) {
                setValue("name", newInputValue);
                setValue("mapPlotRequestId", undefined);
              }
            }}
            onChange={handleChange}
            renderInput={(params) => (
              <TextField
                {...params}
                label="識別名"
                variant="outlined"
                inputProps={{
                  ...params.inputProps,
                  maxLength: 255,
                }}
                error={!!fieldState.error}
                helperText={fieldState.error?.message ?? getHelperMessage()}
              />
            )}
            isOptionEqualToValue={(option, value) =>
              typeof option === "string" || typeof value === "string"
                ? option === value
                : option.value === value.value
            }
          />
        )}
      />
    </Box>
  );
};
